import {type FormEvent, type ReactNode, useState} from "react";

import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {KeyRoundIcon, Loader2Icon, MoreHorizontalIcon, PencilIcon, PlusIcon, Trash2Icon} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {toast} from "sonner";

import {
  type Organization,
  adminOrgsCreate,
  adminOrgsDelete,
  adminOrgsList,
  adminOrgsUpdate,
} from "@/sdk";

import {Button} from "@/components/ui/button";
import {Card, CardContent} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {formatRelative} from "@/lib/format";

const QK = ["admin", "orgs"] as const;

type OrgForm = {
  name: string;
  slug: string;
  blesta_client_id: string;
};

const empty: OrgForm = {
  name: "",
  slug: "",
  blesta_client_id: "",
};

export const AdminOrgs = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [openForm, setOpenForm] = useState<{mode: "create" | "edit"; org?: Organization} | null>(
    null,
  );
  const [form, setForm] = useState<OrgForm>(empty);
  const [deleteTarget, setDeleteTarget] = useState<Organization | null>(null);

  const query = useQuery({
    queryKey: QK,
    queryFn: async () => {
      const {data, error} = await adminOrgsList();
      if (error) throw error;
      return data?.items ?? [];
    },
  });

  const invalidate = () => qc.invalidateQueries({queryKey: QK});

  const createM = useMutation({
    mutationFn: () =>
      adminOrgsCreate({
        body: {
          name: form.name,
          slug: form.slug.trim() || undefined,
          blesta_client_id: form.blesta_client_id.trim() || undefined,
        },
      }),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("Organization created");
      setOpenForm(null);
      invalidate();
    },
  });

  const updateM = useMutation({
    mutationFn: () =>
      adminOrgsUpdate({
        path: {id: openForm!.org!.id},
        body: {
          name: form.name || undefined,
          slug: form.slug.trim() || undefined,
          blesta_client_id: form.blesta_client_id.trim() || undefined,
        },
      }),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("Organization updated");
      setOpenForm(null);
      invalidate();
    },
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => adminOrgsDelete({path: {id}}),
    onSuccess: ({error}) => {
      if (error) {
        toast.error(extractError(error));
        return;
      }
      toast.success("Organization deleted");
      setDeleteTarget(null);
      invalidate();
    },
  });

  const startCreate = () => {
    setForm(empty);
    setOpenForm({mode: "create"});
  };

  const startEdit = (org: Organization) => {
    setForm({
      name: org.name,
      slug: org.slug,
      blesta_client_id: org.blesta_client_id ?? "",
    });
    setOpenForm({mode: "edit", org});
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (openForm?.mode === "create") createM.mutate();
    else updateM.mutate();
  };

  const orgs = query.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Organizations</h1>
          <p className="text-sm text-muted-foreground">
            Customer organizations (tenants). Each org owns its own users, lairs, and servers.
          </p>
        </div>
        <Button onClick={startCreate}>
          <PlusIcon /> Add organization
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="flex items-center justify-center p-12 text-muted-foreground">
              <Loader2Icon className="size-5 animate-spin" />
            </div>
          ) : query.isError ? (
            <div className="p-6 text-sm text-destructive">Failed to load organizations.</div>
          ) : orgs.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No organizations yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Blesta client</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgs.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell>
                      <div className="font-medium">{o.name}</div>
                      <div className="font-mono text-xs text-muted-foreground">{o.id}</div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{o.slug}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {o.blesta_client_id || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatRelative(o.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm">
                            <MoreHorizontalIcon />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => startEdit(o)}>
                            <PencilIcon /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => navigate(`/admin/orgs/${o.id}/api-keys`)}
                          >
                            <KeyRoundIcon /> Manage API keys
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => setDeleteTarget(o)}
                          >
                            <Trash2Icon /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={openForm !== null} onOpenChange={(o) => !o && setOpenForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {openForm?.mode === "create" ? "Create organization" : "Edit organization"}
            </DialogTitle>
            <DialogDescription>
              {openForm?.mode === "create"
                ? "Slug is auto-generated from the name if left blank."
                : "Update display name, slug, or Blesta mapping."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <Field id="name" label="Name">
              <Input
                id="name"
                required
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
              />
            </Field>
            <Field id="slug" label="Slug (optional)">
              <Input
                id="slug"
                placeholder="auto-generated from name"
                value={form.slug}
                onChange={(e) => setForm({...form, slug: e.target.value})}
              />
            </Field>
            <Field id="blesta_client_id" label="Blesta client ID (optional)">
              <Input
                id="blesta_client_id"
                placeholder="e.g. 1234"
                value={form.blesta_client_id}
                onChange={(e) => setForm({...form, blesta_client_id: e.target.value})}
              />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenForm(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createM.isPending || updateM.isPending}>
                {openForm?.mode === "create"
                  ? createM.isPending
                    ? "Creating…"
                    : "Create organization"
                  : updateM.isPending
                    ? "Saving…"
                    : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete organization?</DialogTitle>
            <DialogDescription>
              Soft-deletes <span className="font-mono">{deleteTarget?.name}</span>. Existing
              servers, users, and lairs in this organization will lose access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteM.isPending}
              onClick={() => deleteTarget && deleteM.mutate(deleteTarget.id)}
            >
              {deleteM.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function Field({id, label, children}: {id: string; label: string; children: ReactNode}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

function extractError(err: unknown): string {
  return (err as {detail?: string} | undefined)?.detail ?? "Request failed";
}
